import { lazy } from 'react';
import type { ComponentType } from 'react';

// Retry count for failed imports
const MAX_RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // 1 second

// Track failed imports to avoid infinite loops
const failedImports = new Set<string>();

export function lazyWithRetry<T extends ComponentType<Record<string, unknown>>>(
  componentImport: () => Promise<{ default: T }>,
  componentName?: string
): React.LazyExoticComponent<T> {
  return lazy(async () => {
    const importKey = componentName || componentImport.toString();
    
    // Helper function to retry import
    const tryImport = async (retryCount = 0): Promise<{ default: T }> => {
      try {
        // Clear module cache before retrying
        if (retryCount > 0) {
          console.log(`Retrying import ${componentName || 'component'} (attempt ${retryCount + 1}/${MAX_RETRY_COUNT})`);
        }
        
        const result = await componentImport();
        
        // If successful after retry, remove from failed set
        if (failedImports.has(importKey)) {
          failedImports.delete(importKey);
        }
        
        return result;
      } catch (error: unknown) {
        const errorMessage = error && typeof error === 'object' && 'message' in error ? String(error.message) : '';
        const isChunkError = errorMessage.includes('Failed to fetch dynamically imported module') ||
                           errorMessage.includes('Loading chunk') ||
                           errorMessage.includes('Failed to import') ||
                           errorMessage.includes('ERR_ABORTED');
        
        if (isChunkError && retryCount < MAX_RETRY_COUNT) {
          // Wait before retrying
          await new Promise(resolve => setTimeout(resolve, RETRY_DELAY * (retryCount + 1)));
          
          // Check if user is online
          if (!navigator.onLine) {
            console.warn('User is offline, waiting for connection...');
            await new Promise<void>((resolve) => {
              const handleOnline = () => {
                window.removeEventListener('online', handleOnline);
                resolve();
              };
              window.addEventListener('online', handleOnline);
              
              // Also check periodically in case event doesn't fire
              const checkInterval = setInterval(() => {
                if (navigator.onLine) {
                  clearInterval(checkInterval);
                  window.removeEventListener('online', handleOnline);
                  resolve();
                }
              }, 1000);
            });
          }
          
          return tryImport(retryCount + 1);
        }
        
        // If we've exhausted retries and this is a chunk error, reload the page
        if (isChunkError && !failedImports.has(importKey)) {
          failedImports.add(importKey);

          console.error(`Failed to load ${componentName || 'component'} after ${MAX_RETRY_COUNT} retries. Reloading page...`);

          // Dispatch custom event to show the update notification UI
          // This avoids using browser's native confirm() dialog
          window.dispatchEvent(new CustomEvent('app-chunk-load-failed', {
            detail: { componentName: componentName || 'component' }
          }));
        }
        
        throw error;
      }
    };
    
    return tryImport();
  });
}

// Helper to create lazy imports with retry for named exports
export function lazyWithRetryNamed<T extends ComponentType<Record<string, unknown>>>(
  componentImport: () => Promise<Record<string, unknown>>,
  componentName: string,
  exportName: string
): React.LazyExoticComponent<T> {
  return lazyWithRetry(
    () => componentImport().then(module => ({ default: module[exportName] as T })),
    componentName
  );
}